import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { CircleNotch } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"
import { isNativeApp } from "@/lib/nativeApp"

/**
 * One button, the roles of the palette (DESIGN.md):
 * - primary (default): red, the single commitment action of a screen:
 *   Book, Pay, Select, Sign in on /login. One per view.
 * - ink: strong navigation ("Modifier", filters). Search submits are
 *   primary (red), the owner's rule of 2026-10-02.
 * - outline: ink edge on white, the secondary action ("Edit", "Cancel").
 * - secondary: pale fill, a quiet action inside a card.
 * - ghost: text only until hovered (toolbars, menus).
 * - link: an underlined ink action that is still a 44px target.
 * - destructive: a red edge, not a red block, so cancelling a booking never
 *   looks like booking one.
 *
 * Sizes: default 44 (forms, in-page actions: unchanged), sm 40 (dense desktop
 * only), md 48 (the system's standard button), lg 56 (a search submit, the
 * screen's commitment at the bottom of a form), icon 44 square.
 *
 * Focus is the site-wide ring (index.css :focus-visible, 2px red, 2px off
 * the edge; white on .tk-on-ink). A press scales by 2%, none under
 * prefers-reduced-motion.
 */
const buttonVariants = cva(
  "relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md text-button-md font-semibold transition-[color,background-color,border-color,box-shadow,transform] duration-fast ease-tk-out active:scale-[0.98] motion-reduce:active:scale-100 disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 data-[loading]:cursor-progress data-[loading]:opacity-100 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // The red hovers to --touki-red-dark, the one red hover on every page,
        // and presses to --touki-red-press.
        primary:
          "bg-primary text-primary-foreground hover:bg-touki-red-dark active:bg-touki-red-press",
        default:
          "bg-primary text-primary-foreground hover:bg-touki-red-dark active:bg-touki-red-press",
        ink: "bg-touki-dark text-white hover:bg-touki-dark-hover active:bg-touki-dark-hover",
        outline:
          "border border-touki-black bg-white text-touki-black hover:bg-touki-light active:bg-touki-surface",
        secondary:
          "bg-touki-light text-touki-black hover:bg-touki-surface",
        ghost: "text-touki-black hover:bg-touki-light active:bg-touki-surface",
        link: "text-touki-black underline decoration-touki-field underline-offset-4 hover:decoration-touki-black",
        destructive:
          "border border-touki-red bg-white text-touki-red hover:border-touki-red-dark hover:bg-touki-danger-soft hover:text-touki-red-dark",
      },
      size: {
        default: "h-11 px-4",
        // Smaller box, same 14px label: dense desktop screens only.
        sm: "h-10 px-3",
        md: "h-12 px-5 text-button-lg",
        lg: "h-14 px-6 text-button-lg",
        icon: "h-11 w-11",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      fullWidth: false,
    },
  }
)

/**
 * `loading`: the request is on its way. The label stays in place (invisible,
 * still the button's name) so the width does not change, a spinner covers
 * it, aria-busy says so, and clicks — including the Enter that submits a
 * form — are swallowed so a booking is never sent twice. The button keeps
 * focus (a disabled button would drop it). For a <button> only, not asChild.
 */
const Button = React.forwardRef(({ className, variant, size, fullWidth, loading = false, asChild = false, onClick, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  const busy = Boolean(loading) && !asChild
  const swallow = React.useCallback((event) => { event.preventDefault() }, [])
  return (
    <Comp
      // In the apps a button gives way a little under the thumb (index.css tk-press).
      className={cn(buttonVariants({ variant, size, fullWidth, className }), isNativeApp() && "tk-press")}
      ref={ref}
      onClick={busy ? swallow : onClick}
      {...(busy ? { "aria-busy": true, "aria-disabled": true, "data-loading": "" } : null)}
      {...props}>
      {busy ? (
        <>
          <span className="inline-flex items-center gap-2 opacity-0">{children}</span>
          <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
            <CircleNotch weight="bold" className="animate-spin motion-reduce:[animation-duration:2s]" />
          </span>
        </>
      ) : children}
    </Comp>
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
